import { ReactNode } from 'react';

import {
    CombinedWidgetType,
    ensureWidgetArticleAttachmentType,
    ensureWidgetArticleButtonType,
    ensureWidgetArticleGalleryType,
    ensureWidgetArticleImageType,
    ensureWidgetArticleSliderType,
    ensureWidgetArticleTextType,
    ensureWidgetArticleTitleType,
    ensureWidgetCalendarCalDavType,
    ensureWidgetCheckListType,
    ensureWidgetContactCardDavType,
    ensureWidgetDatasourceTableType,
    ensureWidgetEmailsImapType,
    ensureWidgetExcelDatasourceTableType,
    ensureWidgetLuccaAbsenceType,
    ensureWidgetPresenceType,
    ensureWidgetQuickSurveyType,
    ensureWidgetUserProfileType,
    WidgetDisplayMode,
    WidgetWrapperLevel,
} from 'jamespot-user-api';

import styled from 'styled-components';

export const CSSWidgetWithTitle = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
`;

import { JRCWidgetArticleAttachmentWrapper } from './wrapper/JRCWidgetArticleAttachmentWrapper';
import { JRCWidgetArticleButtonWrapper } from './wrapper/JRCWidgetArticleButtonWrapper';
import { JRCWidgetArticleGalleryWrapper } from './wrapper/JRCWidgetArticleGalleryWrapper';
import { JRCWidgetLuccaAbsenceWrapper } from './wrapper/JRCWidgetLuccaAbsenceWrapper';
import { JRCWidgetArticleImageWrapper } from './wrapper/JRCWidgetArticleImageWrapper';
import { JRCWidgetArticleSliderWrapper } from './wrapper/JRCWidgetArticleSliderWrapper';
import { JRCWidgetArticleTextWrapper } from './wrapper/JRCWidgetArticleTextWrapper';
import { JRCWidgetArticleTitleWrapper } from './wrapper/JRCWidgetArticleTitleWrapper';
import { JRCWidgetCheckListWrapper } from './wrapper/JRCWidgetCheckListWrapper';
import { WidgetDatasourceTableWrapper } from './wrapper/JRCWidgetDatasourceTableWrapper';
import { WidgetExcelDatasourceTableWrapper } from './wrapper/JRCWidgetExcelDatasourceTableWrapper';
import { JRCWidgetPresenceWrapper } from './wrapper/JRCWidgetPresenceWrapper';
import { JRCWidgetQuickSurveyWrapper } from './wrapper/JRCWidgetQuickSurveyWrapper';
import { JRCWidgetUserProfileWrapper } from './wrapper/JRCWidgetUserProfileWrapper';

import { JRCWidgetContactCardDavWrapper } from './wrapper/JRCWidgetContactCardDavWrapper';
import { JRCWidgetCalendarCalDavWrapper } from './wrapper/JRCWidgetCalendarCalDavWrapper';
import { JRCWidgetEmailsImapWrapper } from './wrapper/JRCWidgetEmailsImapWrapper';

import { WidgetTitle } from './wrapper/components/WidgetTitle';

import { WidgetHeaderEdit } from './wrapper/components/WidgetHeaderEdit';
import { WidgetLicenseWrapper } from './wrapper/WidgetLicenseWrapper';

export const DisplayWidget = ({
    widget,
    width,
    inplace = false,
    mode = 'view',
    onWidgetUpdate,
    cannotDisplayComponent,
    levelFallback,
    dataCy,
}: {
    widget: CombinedWidgetType;
    width?: number | undefined;
    inplace: boolean;
    mode: WidgetDisplayMode;
    onWidgetUpdate?: () => void;
    cannotDisplayComponent?: (widget: string) => ReactNode;
    levelFallback?: (level: WidgetWrapperLevel, url: string) => ReactNode | undefined;
    dataCy?: string;
}) => {
    const { name } = widget;

    if (ensureWidgetArticleButtonType(widget)) {
        return <JRCWidgetArticleButtonWrapper widget={widget} inplace={inplace} mode={mode} />;
    } else if (ensureWidgetArticleGalleryType(widget)) {
        return <JRCWidgetArticleGalleryWrapper widget={widget} inplace={inplace} mode={mode} />;
    } else if (ensureWidgetLuccaAbsenceType(widget)) {
        return (
            <CSSWidgetWithTitle>
                <WidgetLicenseWrapper name={'LuccaAbsenceHook'}>
                    <JRCWidgetLuccaAbsenceWrapper uniqid={widget.uniqid} />
                </WidgetLicenseWrapper>
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetArticleAttachmentType(widget)) {
        return <JRCWidgetArticleAttachmentWrapper widget={widget} inplace={inplace} mode={mode} />;
    } else if (ensureWidgetArticleImageType(widget)) {
        return <JRCWidgetArticleImageWrapper widget={widget} inplace={inplace} mode={mode} />;
    } else if (ensureWidgetArticleSliderType(widget)) {
        return <JRCWidgetArticleSliderWrapper widget={widget} inplace={inplace} mode={mode} width={width ?? 800} />;
    } else if (ensureWidgetArticleTitleType(widget)) {
        return <JRCWidgetArticleTitleWrapper widget={widget} inplace={inplace} mode={mode} dataCy={dataCy} />;
    } else if (ensureWidgetArticleTextType(widget)) {
        return <JRCWidgetArticleTextWrapper widget={widget} inplace={inplace} mode={mode} />;
    } else if (ensureWidgetExcelDatasourceTableType(widget)) {
        return (
            <CSSWidgetWithTitle>
                <WidgetTitle uniqid={widget.uniqid} />
                <WidgetExcelDatasourceTableWrapper {...widget.content} uniqid={widget.uniqid} mode={mode} />
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetDatasourceTableType(widget)) {
        return (
            <CSSWidgetWithTitle>
                <WidgetTitle uniqid={widget.uniqid} />
                <WidgetDatasourceTableWrapper {...widget.content} uniqid={widget.uniqid} mode={mode} />
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetCheckListType(widget, widget.name === 'widget-check-list')) {
        return (
            <CSSWidgetWithTitle>
                {mode === 'edit' ? (
                    <WidgetHeaderEdit name={widget.name} uniqid={widget.uniqid} />
                ) : (
                    <WidgetTitle uniqid={widget.uniqid} />
                )}
                <JRCWidgetCheckListWrapper
                    widget={widget}
                    inplace={inplace}
                    mode={mode}
                    onWidgetUpdate={onWidgetUpdate}
                />
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetPresenceType(widget)) {
        return (
            <CSSWidgetWithTitle>
                {mode === 'edit' ? (
                    <WidgetHeaderEdit name={widget.name} uniqid={widget.uniqid} />
                ) : (
                    <WidgetTitle uniqid={widget.uniqid} />
                )}
                <JRCWidgetPresenceWrapper widget={widget} inplace={inplace} mode={mode} />
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetQuickSurveyType(widget)) {
        return (
            <CSSWidgetWithTitle>
                {mode === 'edit' ? (
                    <WidgetHeaderEdit name={widget.name} uniqid={widget.uniqid} />
                ) : (
                    <WidgetTitle uniqid={widget.uniqid} />
                )}
                <JRCWidgetQuickSurveyWrapper widget={widget} inplace={inplace} mode={mode} />
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetCalendarCalDavType(widget)) {
        return (
            <CSSWidgetWithTitle>
                <WidgetLicenseWrapper name={'OfficeHook'}>
                    <JRCWidgetCalendarCalDavWrapper
                        widget={widget}
                        inplace={inplace}
                        mode={mode}
                        levelFallback={levelFallback}
                    />
                </WidgetLicenseWrapper>
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetContactCardDavType(widget)) {
        return (
            <CSSWidgetWithTitle>
                <WidgetLicenseWrapper name={'OfficeHook'}>
                    <JRCWidgetContactCardDavWrapper
                        widget={widget}
                        inplace={inplace}
                        mode={mode}
                        levelFallback={levelFallback}
                    />
                </WidgetLicenseWrapper>
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetEmailsImapType(widget)) {
        return (
            <CSSWidgetWithTitle>
                <WidgetLicenseWrapper name={'OfficeHook'}>
                    <JRCWidgetEmailsImapWrapper
                        widget={widget}
                        inplace={inplace}
                        mode={mode}
                        levelFallback={levelFallback}
                    />
                </WidgetLicenseWrapper>
            </CSSWidgetWithTitle>
        );
    } else if (ensureWidgetUserProfileType(widget, true)) {
        return (
            <CSSWidgetWithTitle>
                <JRCWidgetUserProfileWrapper widget={widget} inplace={inplace} mode={mode} />
            </CSSWidgetWithTitle>
        );
    } else {
        return cannotDisplayComponent ? <>{cannotDisplayComponent(name)}</> : <>cannot display widget ({name})</>;
    }
};
